<template>
  <div v-if="selectedCountryCodeObject" class="phone-number-input">
    <FriedFloater
      class="popper-container"
      :show="open"
      :placement="Placement.BottomEnd"
      same-width-as-ref
    >
      <template #reference>
        <FriedFieldSet
          :label="label"
          active
          :disabled="disabled"
          :success="success"
          :error-message="internalError || errorMessage"
          :help-message="helpMessage"
          class="phone-number-input-container"
        >
          <span class="phone-number-input-field-container">
            <button
              id="country"
              ref="countryButtonRef"
              type="button"
              data-external="contact-country-code-button"
              class="phone-number-input-field-country-button"
              :disabled="disabled"
              @click="handleToggle"
            >
              {{ selectedCountryCodeObject.flag }}
              <FriedIcon
                v-if="!disabled"
                :class="['chevron-icon', { 'point-up': open }]"
                icon="chevron-down"
              />
            </button>
            <span class="phone-number-input-field-country-selected">
              {{ selectedCountryCodeObject.code }}
            </span>
            <input
              class="phone-number-input-field-number-input"
              type="tel"
              data-external="contact-phone-number-input"
              :disabled="disabled"
              :value="number.trim()"
              :autocomplete="autocomplete"
              v-bind="$attrs"
              @input="handleInput"
              @focus="handleClose(false)"
              @blur="handleUpdateNumber"
            />
          </span>
        </FriedFieldSet>
      </template>
      <div
        v-show="open"
        v-click-outside="clickOutside"
        class="phone-number-input-country-code-list"
        data-external="contact-country-code-container"
      >
        <input
          ref="countrySearchRef"
          v-model="search"
          class="phone-number-input-country-code-list-search"
          data-external="contact-country-code-search-input"
          type="text"
          tabindex="-1"
          :placeholder="t('search')"
          @keyup.esc.stop="handleClose(true)"
          @keydown.tab="handleClose(true)"
          @keydown.enter.prevent="handleSelectCountry"
          @keydown.down="handleNavigate"
          @keydown.up="handleNavigate"
        />
        <div class="phone-number-input-country-code-list-options">
          <FriedParagraph v-if="!filteredCountryCodesList.length" class="empty-state">
            {{ t('no-search-results') }}
          </FriedParagraph>
          <template v-else>
            <FriedSelectOption
              v-for="(countryCode, index) of filteredCountryCodesList"
              :key="index"
              :data-external="`phone-number-input-country-code-${countryCode.key}`"
              @click="handleChangeCountryCode(countryCode)"
            >
              {{ countryCode.label }}
            </FriedSelectOption>
          </template>
        </div>
      </div>
    </FriedFloater>
  </div>
</template>

<script lang="ts">
import type { PhoneNumber, CountryCode } from 'libphonenumber-js';
import parsePhoneNumberFromString from 'libphonenumber-js';
import debounce from 'debounce';
import { countryCodes } from '@getaccept/lib-shared-new/src/countries/constants/country-codes';
import type { CountryCodeObj } from '@getaccept/lib-shared-new/src/countries/constants/country-code';
import { defineComponent, ref, computed, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { Placement } from '@getaccept/fried-tofu';
import { testMobileNumber } from '../helpers';

export default defineComponent({
  model: {
    prop: 'model-value',
    event: 'update:model-value',
  },
  props: {
    defaultCountryCode: {
      type: String,
      default: '',
    },
    label: {
      type: String,
      default: '',
    },
    modelValue: {
      type: String,
      default: '',
    },
    helpMessage: {
      type: String,
      default: '',
    },
    errorMessage: {
      type: String,
      default: '',
    },
    success: {
      type: Boolean,
    },
    disabled: {
      type: Boolean,
    },
    required: {
      type: Boolean,
    },
    autocomplete: {
      type: String,
      default: '',
    },
  },
  emits: ['error', 'update:model-value'],
  setup(props, { emit }) {
    const { t } = useI18n();
    const countrySearchRef = ref<HTMLInputElement>(null);
    const countryButtonRef = ref<HTMLButtonElement>(null);

    const selectedCountryCode = ref('');
    const open = ref(false);
    const number = ref('');
    const search = ref('');
    const fallbackCountryCode = ref('SE');
    const dropdownFocus = ref(0);

    const watchSearch = () => {
      dropdownFocus.value = 0;
    };

    watch(search, watchSearch);

    const countryCodesList = computed(() =>
      countryCodes.map((country: CountryCodeObj) => ({
        key: country.key,
        label: `${country.flag} (${country.code}) ${country.label}`,
        code: country.code,
        flag: country.flag,
      }))
    );

    const selectedCountryCodeObject = computed(() => {
      if (selectedCountryCode.value) {
        return countryCodesList.value.find(
          countryCodeObject => countryCodeObject.key === selectedCountryCode.value
        );
      }
      const fallbackCountryCodeObject = countryCodesList.value.find(
        countryCodeObject => countryCodeObject.key === fallbackCountryCode.value
      );
      if (props.defaultCountryCode) {
        return (
          countryCodesList.value.find(
            countryCodeObject => countryCodeObject.key === props.defaultCountryCode
          ) || fallbackCountryCodeObject
        );
      }
      return fallbackCountryCodeObject;
    });

    const fullNumber = computed(() => {
      const numberObject: PhoneNumber = parsePhoneNumberFromString(
        number.value,
        selectedCountryCodeObject.value.key as CountryCode
      );
      if (numberObject) {
        return numberObject.number.toString();
      }
      return number.value;
    });

    const internalError = computed(() => {
      if (!number.value && !props.required) {
        return '';
      }
      return testMobileNumber(fullNumber.value) ? '' : t('enter-a-valid-phone-number');
    });

    const watchInternalError = () => {
      emit('error', internalError.value);
    };

    watch(internalError, watchInternalError, { immediate: true });

    const handleInput: (value: any) => void = debounce(value => handleUpdateNumber(value), 300);

    const clickOutside = (event: any) => {
      const countrySearchFieldClicked: boolean = event.target === countrySearchRef.value;
      if (!open.value || countrySearchFieldClicked) {
        return;
      }
      handleClose(false);
    };

    const handleToggle = async () => {
      if (open.value) {
        handleClose(false);
        return;
      }
      open.value = true;
      await nextTick();
      countrySearchRef.value?.focus();
    };

    const handleClose = (keepFocus = true) => {
      open.value = false;
      search.value = '';
      if (keepFocus) {
        countryButtonRef.value?.focus();
      }
    };

    const handleChangeCountryCode = (item: CountryCodeObj) => {
      if (!item?.key) {
        return;
      }
      selectedCountryCode.value = item.key;
      handleClose();
      if (!number.value) {
        return;
      }
      emit('update:model-value', fullNumber.value);
    };

    const handleNavigate = (event: KeyboardEvent) => {
      event.preventDefault();

      const firstSelected: boolean = dropdownFocus.value === 0;
      const lastSelected: boolean = dropdownFocus.value === countryCodesList.value.length - 1;
      switch (event.key) {
        case 'ArrowUp':
          if (firstSelected) {
            return;
          }
          dropdownFocus.value--;
          break;
        case 'ArrowDown':
          if (lastSelected) {
            return;
          }
          dropdownFocus.value++;
          break;
      }
    };

    const setPhoneNumberValues = (phoneNumber: string) => {
      const numberObject: PhoneNumber = parsePhoneNumberFromString(
        phoneNumber,
        selectedCountryCodeObject.value?.key as CountryCode
      );
      if (numberObject) {
        number.value = numberObject.nationalNumber.toString();
        if (numberObject.country) {
          selectedCountryCode.value = numberObject.country;
        }
      } else {
        number.value = phoneNumber;
      }
    };

    const handleUpdateNumber = (event: any) => {
      handleClose(false);
      const number = event.target.value;
      setPhoneNumberValues(number);
      emit('update:model-value', fullNumber.value);
    };

    const watchValue = (newValue: string) => {
      setPhoneNumberValues(newValue);
      if (!!newValue && newValue.charAt(0) !== '+') {
        emit('update:model-value', fullNumber.value);
      }
    };

    watch(() => props.modelValue, watchValue, { immediate: true });

    const clearNumber = () => {
      number.value = '';
    };

    const handleSelectCountry = () => {
      if (countryCodesList.value.length) {
        handleChangeCountryCode(countryCodesList.value[dropdownFocus.value]);
      }
    };

    const filteredCountryCodesList = computed(() =>
      countryCodesList.value.filter(country => {
        if (!search.value) {
          return country;
        }
        return country.label.toLowerCase().includes(search.value.toLowerCase());
      })
    );

    return {
      countrySearchRef,
      countryButtonRef,
      selectedCountryCode,
      open,
      number,
      search,
      fallbackCountryCode,
      dropdownFocus,
      fullNumber,
      selectedCountryCodeObject,
      handleInput,
      clickOutside,
      handleToggle,
      handleClose,
      handleChangeCountryCode,
      handleNavigate,
      handleUpdateNumber,
      clearNumber,
      internalError,
      handleSelectCountry,
      filteredCountryCodesList,
      countryCodesList,
      setPhoneNumberValues,
      watchValue,
      watchSearch,
      watchInternalError,
      t,
      Placement,
    };
  },
});
</script>

<style lang="scss" scoped>
.phone-number-input {
  &-field {
    width: 100%;

    &-container {
      display: grid;
      grid-template-columns: auto auto 1fr;
      align-items: center;
      gap: var(--spacing-100);
      width: 100%;
      padding: 0 var(--spacing-100);
    }

    &-country-button {
      border: 0;
      border-right: 1px solid var(--gray-89);
      padding: 0 var(--spacing-100) 0 0;
      display: flex;
      align-items: center;
      background-color: transparent;
      outline: none;
      gap: var(--spacing-100);

      .chevron-icon {
        transition: transform 0.3s;

        &.point-up {
          transform: rotate(180deg);
        }
      }
    }

    &-country-selected {
      color: var(--text-gray);
      cursor: default;
    }

    &-number-input {
      outline: 0;
      border: none;
      background: none;
      color: var(--text-dark);

      &[data-com-onepassword-filled] {
        transition: background-color 500000s;
      }

      &:-webkit-autofill {
        // Exposes a JS hook for when input get autofilled
        animation-name: onAutoFillStart;
        transition: background-color 500000s;
      }
    }
  }

  &-country-code-list {
    display: flex;
    flex-direction: column;
    box-shadow: var(--shadow-3);
    border-radius: var(--radius-small);
    z-index: 1;

    &-search {
      padding: var(--spacing-75) var(--spacing-100);
      outline: none;
      border: none;
      border-bottom: 1px solid var(--gray-89);
    }

    &-options {
      max-height: 15rem;
      overflow-y: auto;
      background-color: var(--white);
      padding: var(--spacing-50);
      border-radius: 0 0 var(--radius-small) var(--radius-small);

      .empty-state {
        text-align: center;
        padding: var(--spacing-50);
      }
    }
  }

  .popper-container {
    width: 100%;

    :deep(.input-outer-container) {
      width: inherit;
    }
  }
}
</style>
